import { useEffect, useRef, useState } from 'react';

interface Chapter {
  number: string;
  title: string;
  description: string;
  status: 'complete' | 'progress' | 'upcoming';
  rune: string;
  image: string;
}

export default function RoadmapSection() {
  const [visibleChapters, setVisibleChapters] = useState<number[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);
  const chapterRefs = useRef<(HTMLDivElement | null)[]>([]);

  const chapters: Chapter[] = [
    {
      number: 'I',
      title: 'Birth of the Flame',
      description: 'The genesis of VALHALLA begins. Token creation, smart contract deployment, and the first warriors gather at the gates. The foundation is laid in fire and code.',
      status: 'complete',
      rune: 'ᚠ',
      image: 'https://public.readdy.ai/ai/img_res/9016d9c507b2ab40eeeb41a5f9b18114.jpg'
    },
    {
      number: 'II',
      title: 'March of the Berserkers',
      description: 'Community building and marketing campaigns ignite across the realm. Social media presence expands, partnerships form, and the army of believers grows stronger.',
      status: 'progress',
      rune: 'ᚢ',
      image: 'https://readdy.ai/api/search-image?query=massive%20viking%20army%20marching%20forward%20berserker%20warriors%20charging%20battle%20formation%20shields%20raised%20war%20banners%20flying%20dramatic%20sky%20lightning%20strikes%20epic%20cinematic%20composition%20hundreds%20of%20warriors%20nordic%20armor%20helmets%20spears%20atmospheric%20fog%20ground%20level%20perspective%20powerful%20movement&width=800&height=600&seq=viking-march-002&orientation=landscape'
    },
    {
      number: 'III',
      title: 'The Gates Open',
      description: 'Major exchange listings and liquidity expansion. The gates of Valhalla swing wide as trading volume surges and new warriors flood the halls.',
      status: 'upcoming',
      rune: 'ᚦ',
      image: 'https://readdy.ai/api/search-image?query=massive%20golden%20valhalla%20gates%20opening%20wide%20glowing%20divine%20light%20beams%20pouring%20through%20ancient%20carved%20runes%20on%20stone%20pillars%20mystical%20energy%20particles%20floating%20warriors%20entering%20majestic%20portal%20dramatic%20atmospheric%20lighting%20cinematic%20perspective%20epic%20scale%20norse%20mythology%20entrance&width=800&height=600&seq=viking-gates-003&orientation=landscape'
    },
    {
      number: 'IV',
      title: 'Valhalla Ascends',
      description: 'Full ecosystem launch with staking, NFT collections, and governance. The hall reaches its final form—a self-sustaining realm of eternal rewards.',
      status: 'upcoming',
      rune: 'ᚨ',
      image: 'https://readdy.ai/api/search-image?query=magnificent%20valhalla%20great%20hall%20interior%20golden%20throne%20room%20viking%20warriors%20feasting%20celebrating%20mead%20horns%20raised%20glowing%20runes%20on%20walls%20torches%20burning%20epic%20architecture%20carved%20wooden%20beams%20shields%20on%20walls%20atmospheric%20lighting%20cinematic%20composition%20norse%20mythology%20eternal%20paradise&width=800&height=600&seq=viking-ascend-004&orientation=landscape'
    }
  ];

  useEffect(() => {
    const observers = chapterRefs.current.map((ref, index) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleChapters(prev => [...new Set([...prev, index])]);
          }
        },
        { threshold: 0.3 }
      );

      if (ref) observer.observe(ref);
      return observer;
    });

    return () => observers.forEach(observer => observer.disconnect());
  }, []);

  return (
    <section ref={sectionRef} className="relative py-32 bg-gradient-to-b from-black via-[#1a0033] to-black overflow-hidden">
      {/* Animated star field */}
      <div className="absolute inset-0">
        {Array.from({ length: 100 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full animate-star-fall"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${10 + Math.random() * 10}s`,
              opacity: Math.random() * 0.7 + 0.3
            }}
          />
        ))}
      </div>

      {/* Northern lights overlay */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute w-full h-full bg-gradient-to-b from-green-500/30 via-blue-500/30 to-transparent animate-aurora-vertical" />
      </div>

      {/* Flying ravens */}
      <div className="absolute top-1/4 left-0 w-full h-32 pointer-events-none overflow-hidden">
        <i className="ri-contrast-drop-fill absolute text-black text-4xl opacity-60 animate-raven-fly" style={{ animationDuration: '20s' }}></i>
        <i className="ri-contrast-drop-fill absolute text-black text-3xl opacity-40 animate-raven-fly" style={{ animationDuration: '25s', animationDelay: '5s' }}></i>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-24">
          <h2 className="text-6xl lg:text-7xl font-bold text-white mb-4 tracking-wide" style={{ fontFamily: 'Cinzel, serif' }}>
            THE VIKING SAGA
          </h2>
          <p className="text-[#d4af37] text-xl">Four Chapters of Destiny</p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Central spine */}
          <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-[#d4af37] via-[#ffed4e] to-[#d4af37] -translate-x-1/2 hidden lg:block" />

          {/* Chapters */}
          <div className="space-y-32">
            {chapters.map((chapter, index) => {
              const isLeft = index % 2 === 0;
              const isVisible = visibleChapters.includes(index);

              return (
                <div
                  key={index}
                  ref={el => chapterRefs.current[index] = el}
                  className="relative"
                >
                  {/* Timeline node */}
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden lg:block z-20">
                    <div className={`w-32 h-32 rounded-full border-4 flex items-center justify-center transition-all duration-500 ${
                      chapter.status === 'complete' 
                        ? 'bg-gradient-to-br from-[#d4af37] to-[#ffed4e] border-[#d4af37] animate-pulse-glow' 
                        : chapter.status === 'progress'
                        ? 'bg-black border-[#d4af37] animate-pulse-glow'
                        : 'bg-black border-[#d4af37] opacity-50'
                    }`}>
                      <span className={`text-5xl ${chapter.status === 'complete' ? 'text-black' : 'text-[#d4af37]'}`}>
                        {chapter.rune}
                      </span>
                    </div>
                  </div>

                  {/* Chapter card */}
                  <div className={`lg:w-1/2 ${isLeft ? 'lg:pr-24' : 'lg:pl-24 lg:ml-auto'}`}>
                    <div
                      className={`relative bg-black/80 backdrop-blur-sm border-2 rounded-2xl overflow-hidden transition-all duration-1000 ${
                        isLeft ? 'border-l-[#d4af37] border-r border-t border-b border-gray-800' : 'border-r-[#d4af37] border-l border-t border-b border-gray-800'
                      } ${
                        isVisible 
                          ? 'opacity-100 translate-x-0' 
                          : `opacity-0 ${isLeft ? '-translate-x-12' : 'translate-x-12'}`
                      }`}
                    >
                      {/* Connecting line to timeline */}
                      <div className={`absolute top-1/2 w-12 h-0.5 bg-[#d4af37] hidden lg:block z-10 ${
                        isLeft ? 'right-0 translate-x-full' : 'left-0 -translate-x-full'
                      }`} />

                      {/* Chapter image */}
                      <div className="relative w-full h-64 overflow-hidden">
                        <img
                          src={chapter.image}
                          alt={chapter.title}
                          className="w-full h-full object-cover object-top"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/90" />
                      </div>

                      {/* Content */}
                      <div className="p-12">
                        {/* Chapter number */}
                        <p className="text-[#d4af37] text-sm uppercase tracking-[0.3em] mb-4 font-semibold">
                          CHAPTER {chapter.number}
                        </p>

                        {/* Title */}
                        <h3 className="text-4xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Cinzel, serif' }}>
                          {chapter.title}
                        </h3>

                        {/* Description */}
                        <p className="text-[#b8b8b8] text-base leading-relaxed mb-8">
                          {chapter.description}
                        </p>

                        {/* Status badge */}
                        <div className="flex justify-end">
                          <div className={`px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wider ${
                            chapter.status === 'complete'
                              ? 'bg-[#d4af37] text-black'
                              : chapter.status === 'progress'
                              ? 'border-2 border-[#d4af37] text-[#d4af37]'
                              : 'border-2 border-gray-600 text-gray-600'
                          }`}>
                            {chapter.status === 'complete' ? 'COMPLETE' : chapter.status === 'progress' ? 'IN PROGRESS' : 'UPCOMING'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
